import React, { useEffect, useState } from 'react';
import { http, authCfg } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { Loader2, Rocket, RefreshCw, Zap, Save, BellRing } from 'lucide-react';

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] tracking-[0.2em] uppercase text-alp-gray">{label}</span>
    {children}
  </label>
);

const inputCls = "mt-1 w-full bg-alp-dark border border-alp-line rounded px-3 py-2 text-sm text-alp-cream focus:border-alp-gold outline-none";

export default function BlackFridayPanel() {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [reminding, setReminding] = useState(false);

  const load = () => http.get('/admin/blackfriday', authCfg()).then((r) => { setData(r.data); setDraft(r.data.config); }).catch(() => setData({ config: {}, signups: [], stats: {} }));
  useEffect(() => { load(); }, []);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await http.put('/admin/blackfriday/config', {
        percent: Number(draft.percent), earlyBirdPercent: Number(draft.earlyBirdPercent),
        earlyBirdLimit: Number(draft.earlyBirdLimit), launchAt: draft.launchAt, expiry: draft.expiry,
      }, authCfg());
      toast({ title: 'Black Friday settings saved' });
      load();
    } catch (e) { toast({ title: 'Could not save', variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const launch = async () => {
    const pendingCount = data.stats.total - data.stats.sent;
    if (!window.confirm(`GO LIVE? This emails a personal single-use code to ${pendingCount} member${pendingCount === 1 ? '' : 's'} (${draft.percent}% / ${draft.earlyBirdPercent}% early bird), expiring ${draft.expiry}. This can't be unsent.`)) return;
    setLaunching(true);
    try {
      const { data: res } = await http.post('/admin/blackfriday/launch', {}, authCfg());
      toast({ title: `🚀 Live! ${res.sentCount} code${res.sentCount === 1 ? '' : 's'} sent` });
      load();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Launch failed', variant: 'destructive' }); }
    finally { setLaunching(false); }
  };

  const remind = async () => {
    const waiting = (data.signups || []).filter((s) => !s.code && !s.remindedAt).length;
    if (!window.confirm(`Send the "24 hours to go" reminder to ${waiting} member${waiting === 1 ? '' : 's'}? Each person only ever gets it once.`)) return;
    setReminding(true);
    try {
      const { data: res } = await http.post('/admin/blackfriday/remind', {}, authCfg());
      toast({ title: `⏳ Reminder sent to ${res.sentCount} member${res.sentCount === 1 ? '' : 's'}` });
      load();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Could not send reminders', variant: 'destructive' }); }
    finally { setReminding(false); }
  };

  if (!data || !draft) return <div className="py-16 text-center"><Loader2 className="animate-spin inline text-alp-gold" size={20} /></div>;
  const st = data.stats || {};
  const rep = data.report || {};
  const pending = (st.total || 0) - (st.sent || 0);
  const remindable = (data.signups || []).filter((s) => !s.code && !s.remindedAt).length;

  return (
    <div data-testid="blackfriday-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h2 className="font-serif text-2xl text-alp-cream flex items-center gap-2">🖤 Black Friday</h2>
        <button onClick={load} className="inline-flex items-center gap-1.5 text-xs tracking-[0.12em] uppercase border border-alp-line px-3 py-1.5 rounded text-alp-cream hover:border-alp-gold hover:text-alp-gold"><RefreshCw size={13} /> Refresh</button>
      </div>
      <p className="text-alp-gray text-sm mb-6">Members join the list at <span className="text-alp-gold">/black-friday</span>. When you press GO LIVE, each gets a personal single-use code locked to their email, expiring {draft.expiry}.</p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="bf-admin-stats">
        {[['On the list', st.total || 0], ['Early birds', st.earlyBird || 0], ['Codes sent', st.sent || 0], ['Waiting', pending]].map(([l, v]) => (
          <div key={l} className="border border-alp-line rounded-lg bg-alp-card/40 p-4">
            <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gray">{l}</p>
            <p className="text-alp-gold font-serif text-xl mt-1">{v}</p>
          </div>
        ))}
      </div>

      {(rep.codesSent || 0) > 0 && (
        <div className="border border-alp-gold/40 rounded-lg bg-alp-card/40 p-5 mb-8" data-testid="bf-report">
          <p className="text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-3">Black Friday results</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[['Codes used', `${rep.codesUsed || 0} of ${rep.codesSent || 0}`], ['Redemption rate', `${rep.codesSent ? Math.round(((rep.codesUsed || 0) / rep.codesSent) * 100) : 0}%`], ['Orders', rep.orders || 0], ['Revenue', `$${Number(rep.revenue || 0).toFixed(2)}`]].map(([l, v]) => (
              <div key={l}>
                <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gray">{l}</p>
                <p className="text-alp-cream font-serif text-xl mt-1">{v}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border border-alp-line rounded-lg bg-alp-card/40 p-5 mb-8 max-w-3xl">
        <p className="text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-4">Settings</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Field label="Discount %"><input type="number" value={draft.percent} onChange={(e) => setDraft({ ...draft, percent: e.target.value })} className={inputCls} data-testid="bf-percent-input" /></Field>
          <Field label="Early bird %"><input type="number" value={draft.earlyBirdPercent} onChange={(e) => setDraft({ ...draft, earlyBirdPercent: e.target.value })} className={inputCls} data-testid="bf-eb-percent-input" /></Field>
          <Field label="Early bird spots"><input type="number" value={draft.earlyBirdLimit} onChange={(e) => setDraft({ ...draft, earlyBirdLimit: e.target.value })} className={inputCls} data-testid="bf-eb-limit-input" /></Field>
          <Field label="Launch (countdown target)"><input type="datetime-local" value={(draft.launchAt || '').slice(0, 16)} onChange={(e) => setDraft({ ...draft, launchAt: e.target.value ? e.target.value + ':00+11:00' : draft.launchAt })} className={inputCls} data-testid="bf-launch-input" /></Field>
          <Field label="Code expiry"><input type="datetime-local" value={(draft.expiry || '').slice(0, 16)} onChange={(e) => setDraft({ ...draft, expiry: e.target.value ? e.target.value + ':00+11:00' : draft.expiry })} className={inputCls} data-testid="bf-expiry-input" /></Field>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-5">
          <button onClick={saveConfig} disabled={saving} data-testid="bf-save-config"
            className="inline-flex items-center gap-1.5 border border-alp-gold text-alp-gold px-4 py-2 rounded text-xs tracking-[0.12em] uppercase hover:bg-alp-gold hover:text-alp-black disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save settings
          </button>
          <button onClick={remind} disabled={reminding || remindable === 0} data-testid="bf-remind-btn"
            className="inline-flex items-center gap-1.5 border border-alp-line text-alp-cream px-4 py-2 rounded text-xs tracking-[0.12em] uppercase hover:border-alp-gold hover:text-alp-gold disabled:opacity-40">
            {reminding ? <Loader2 size={14} className="animate-spin" /> : <BellRing size={14} />} 24h reminder ({remindable})
          </button>
          <button onClick={launch} disabled={launching || pending === 0} data-testid="bf-launch-btn"
            className="inline-flex items-center gap-2 bg-alp-gold text-alp-black px-6 py-2.5 rounded text-xs tracking-[0.15em] uppercase font-bold hover:bg-alp-goldlight disabled:opacity-40">
            {launching ? <Loader2 size={14} className="animate-spin" /> : <Rocket size={15} />} {data.config.live ? `Send to ${pending} new signup${pending === 1 ? '' : 's'}` : 'GO LIVE — send all codes'}
          </button>
          {data.config.live && <span className="text-xs text-green-400">● Live — new signups can still be sent codes</span>}
        </div>
      </div>

      <div className="border border-alp-line rounded-lg bg-alp-card/40 p-5">
        <p className="text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-3">Signups</p>
        {(data.signups || []).length === 0 ? <p className="text-alp-gray text-sm py-4 text-center">No signups yet — share /black-friday with your clients.</p> : (
          <div className="border border-alp-line rounded divide-y divide-alp-line/50 max-h-[420px] overflow-y-auto">
            {data.signups.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5 text-sm" data-testid={`bf-signup-${s.email}`}>
                <span className="text-alp-gray text-xs w-8">#{s.rank}</span>
                <span className="min-w-0 flex-1">
                  <span className="block text-alp-cream">{s.name || s.email}</span>
                  <span className="block text-alp-gray text-xs">{s.email}</span>
                </span>
                {s.earlyBird && <span className="inline-flex items-center gap-1 text-[10px] text-alp-gold border border-alp-gold/50 rounded-full px-2 py-0.5"><Zap size={10} /> EARLY BIRD</span>}
                {s.remindedAt && !s.code && <span className="text-[10px] text-alp-gray border border-alp-line rounded-full px-2 py-0.5">reminded</span>}
                {s.used && <span className="text-[10px] font-bold text-green-400 border border-green-500/50 rounded-full px-2 py-0.5">USED ✓</span>}
                {s.code ? <span className="text-xs text-green-400 font-mono">{s.code} · {s.percent}% · {s.emailStatus}</span> : <span className="text-xs text-alp-gray">waiting for launch</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
